import { X } from 'lucide-react';
import { type KeyboardEvent, type MouseEvent } from 'react';

import { cn } from '@heidi/utils';

import { Button } from '../button';
import { useDesignSystemIntl } from '../hooks/use-design-system-intl';

interface FileCardDeleteButtonProps {
  onDelete: (e: MouseEvent<HTMLButtonElement>) => void;
  ariaLabel?: string;
  /**
   * Skip the hover reveal and keep the control visible. Touch devices have
   * no hover, so the mobile error card shows it outright.
   */
  alwaysVisible?: boolean;
}

export const FileCardDeleteButton = ({
  onDelete,
  ariaLabel,
  alwaysVisible = false,
}: FileCardDeleteButtonProps) => {
  const intl = useDesignSystemIntl();
  const defaultDeleteLabel = intl.formatMessage({
    defaultMessage: 'Delete file',
    id: 'Xco/n69ZkQ',
    description: 'Default accessible label for the FileCard delete button',
  });
  const deleteLabel = ariaLabel ?? defaultDeleteLabel;

  const handleDeleteClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onDelete(event);
  };

  const handleDeleteKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  };

  return (
    // The glyph box stays 20×20 by design; the invisible `::after` overlay
    // lifts the pointer target to 24×24 for WCAG 2.5.8.
    //
    // The overlay grows only up and toward the end edge — the two directions
    // the button already overhangs the card. Growing it inward (`-inset-1`)
    // would push it further over the error variant's retry button, which the
    // 20px box already clips by ~5px at the corner; a destructive control
    // should not take more of retry's hit area than it already does.
    //
    // No `relative` needed — the button is itself `absolute` (and
    // transformed), so it already establishes the containing block.
    <Button
      iconOnly
      className={cn(
        "absolute end-0 top-0 size-5 min-w-5 -translate-y-1/3 translate-x-1/3 rounded-full p-0.5 after:absolute after:-end-1 after:-top-1 after:bottom-0 after:start-0 after:content-[''] hover:opacity-100 focus:opacity-100 group-hover:opacity-100 rtl:-translate-x-1/3",
        alwaysVisible ? 'opacity-100' : 'opacity-0'
      )}
      size="xxs"
      onClick={handleDeleteClick}
      onKeyDown={handleDeleteKeyDown}
      aria-label={deleteLabel}
      data-testid="file-card-delete-button"
      variant="destructive"
    >
      <X />
    </Button>
  );
};
